// AdvancedBulkInputDialog.tsx
import {
  useSearchQueryFilter,
  useSearchRouteInfo,
  useTrackEvents,
} from '@aibees/elephant/hooks';
import { FormDialog, FormDialogSubmitHandler } from '@aibees/shared/components';
import { classnames, encodeQueryParamValues } from '@aibees/shared/utils';
import { memo, useMemo, useState } from 'react';
import { parseCommaSeparatedValues } from '../../utils';
import { BulkInputDialogContent } from './bulk-input-dialog-content';
import { createConfig, MAX_VALUES } from './config';
import * as SX from './sx';
import type { AdvancedBulkInputDialogProps as Props } from './types';
import { getFormattedValue } from './utils';

const DIALOG_PAPER_PROPS = { sx: SX.dialogPaper };

export const AdvancedBulkInputDialog = memo(
  ({
    className,
    field,
    dialogState,
    metadata,
    filterType,
    filterName,
  }: Props) => {
    const { setParam, params } = useSearchQueryFilter({ select: [field] });
    const { type } = useSearchRouteInfo();
    const { track } = useTrackEvents();

    const paramValue = params[field];

    const formattedParamValue = useMemo(
      () => getFormattedValue(paramValue),
      [paramValue]
    );

    const defaultValues = useMemo(
      () => ({ [field]: formattedParamValue }),
      [field, formattedParamValue]
    );

    const [hasChanges, setHasChanges] = useState(false);
    const [hasMaxValuesError, setHasMaxValuesError] = useState(false);

    const handleSubmit: FormDialogSubmitHandler = (data, state) => {
      const inputValue = ((data[field] as string) ?? '').trim();

      let nextEncoded: string | undefined;
      let trackedValues: string[] = [];

      if (inputValue) {
        const parsedValues = parseCommaSeparatedValues(inputValue);
        const uniqueValues = Array.from(new Set(parsedValues));
        trackedValues = uniqueValues.slice(0, MAX_VALUES);
        if (trackedValues.length > 0) {
          nextEncoded = encodeQueryParamValues(trackedValues);
        }
      }

      state.toggle();

      setTimeout(() => {
        setParam(field, nextEncoded);

        if (nextEncoded && type && filterType && filterName) {
          track('FILTER', {
            filterName,
            filterType,
            filterValue: trackedValues,
            searchType: type,
          });
        }
      }, 0);
    };

    // Empty submit is only valid when there is an existing value to clear.
    const hasExistingValue = Boolean(formattedParamValue.trim());

    const baseConfig = useMemo(
      () =>
        createConfig(
          metadata?.dialog?.title || 'Bulk Include Items',
          field,
          metadata?.dialog?.icon,
          hasExistingValue
        ),
      [metadata?.dialog?.title, field, metadata?.dialog?.icon, hasExistingValue]
    );

    const config = useMemo(
      () => ({
        ...baseConfig,
        submitButtonProps: {
          ...baseConfig.submitButtonProps,
          disabled: !hasChanges || hasMaxValuesError,
        },
      }),
      [baseConfig, hasChanges, hasMaxValuesError]
    );

    return (
      <FormDialog
        className={classnames('AdvancedBulkInputDialog', className)}
        state={dialogState}
        {...config}
        defaultValues={defaultValues}
        onSubmit={handleSubmit}
        maxWidth="sm"
        resetAfterClose={false}
        PaperProps={DIALOG_PAPER_PROPS}
        customLeftActions={metadata?.dialog?.customLeftActions}
      >
        <BulkInputDialogContent
          field={field}
          dialogState={dialogState}
          metadata={metadata}
          onValueChange={setHasChanges}
          onMaxValuesErrorChange={setHasMaxValuesError}
          currentValue={formattedParamValue}
        />
      </FormDialog>
    );
  }
);

AdvancedBulkInputDialog.displayName = 'AdvancedBulkInputDialog';
